import React from "react";
import { ChevronLeftIcon } from "@heroicons/react/24/outline";
import { Link } from "react-router-dom";

export default function DashboardDocumentation() {
    return (
        <section className="max-w-5xl mx-auto py-12 px-4 lg:px-8 space-y-16">
            {/* Hero Header */}
            <div className="text-center border-b border-gray-200 pb-12">
                <h1 className="text-5xl font-extrabold text-gray-900 mb-6">
                    Boat Surveyors Dashboard Documentation
                </h1>
                <p className="text-2xl text-gray-600 max-w-4xl mx-auto leading-relaxed">
                    The ultimate, in-depth reference guide to mastering every corner of the Boat Surveyors management platform.
                    Designed to empower administrators, surveyors, and managers with crystal-clear insights into workflows,
                    financial tracking, client relationships, and operational excellence.
                </p>
            </div>

            {/* Table of Contents */}
            <div className="bg-gradient-to-r from-blue-50 to-emerald-50 rounded-3xl p-10 shadow-lg">
                <h2 className="text-3xl font-bold text-gray-900 mb-8">Table of Contents</h2>
                <ol className="grid md:grid-cols-2 gap-6 text-lg text-gray-800">
                    <li><a href="#dashboard" className="text-blue-700 hover:underline font-medium">1. Dashboard – Your Command Center</a></li>
                    <li><a href="#clients" className="text-blue-700 hover:underline font-medium">2. Clients Management – The Heart of Relationships</a></li>
                    <li><a href="#requests" className="text-blue-700 hover:underline font-medium">3. Requests – From Inquiry to Action</a></li>
                    <li><a href="#quotes" className="text-blue-700 hover:underline font-medium">4. Quotes – Professional Proposals That Win</a></li>
                    <li><a href="#jobs" className="text-blue-700 hover:underline font-medium">5. Jobs – Field Operations Mastery</a></li>
                    <li><a href="#invoices" className="text-blue-700 hover:underline font-medium">6. Invoices – Seamless Financial Closure</a></li>
                    <li><a href="#schedule" className="text-blue-700 hover:underline font-medium">7. Schedule & Calendar – Intelligent Time Management</a></li>
                    <li><a href="#surveyors" className="text-blue-700 hover:underline font-medium">8. Surveyors & Payouts – Empowering Your Team</a></li>
                    <li><a href="#reports" className="text-blue-700 hover:underline font-medium">9. Reports – Archiving Excellence</a></li>
                    <li><a href="#categories" className="text-blue-700 hover:underline font-medium">10. Categories & Subcategories – Structured Expertise</a></li>
                    <li><a href="#settings" className="text-blue-700 hover:underline font-medium">11. Settings – Tailoring the System to Perfection</a></li>
                    <li><a href="#notifications" className="text-blue-700 hover:underline font-medium">12. Notifications – Stay Ahead in Real Time</a></li>
                    <li><a href="#lead-sources" className="text-blue-700 hover:underline font-medium">13. Lead Sources – Marketing Intelligence</a></li>
                    <li><a href="#offers-management" className="text-blue-700 hover:underline font-medium">14. Offers Management – Proactive Sales</a></li>
                    <li><a href="#payments" className="text-blue-700 hover:underline font-medium">15. My Payments – Cash Flow Control</a></li>
                    <li><a href="#problem-reports" className="text-blue-700 hover:underline font-medium">16. Problem Reports – Field Issue Resolution</a></li>
                    <li><a href="#team-members" className="text-blue-700 hover:underline font-medium">17. Team Members – Access & Roles</a></li>
                    <li><a href="#user-agreements" className="text-blue-700 hover:underline font-medium">18. User Agreements – Contract Archive</a></li>
                    <li><a href="#contact-messages" className="text-blue-700 hover:underline font-medium">19. Contact Messages – Centralized Inbox</a></li>
                    <li><a href="#live-chat" className="text-blue-700 hover:underline font-medium">20. Live Chat – Real-Time Support</a></li>
                    <li><a href="#tips" className="text-blue-700 hover:underline font-medium">21. Pro Tips & Best Practices</a></li>
                </ol>
            </div>

            {/* Detailed Sections */}
            <div className="space-y-20 prose prose-xl max-w-none text-gray-700">
                {/* 1. Dashboard */}
                <div id="dashboard" className="bg-white rounded-2xl shadow-md p-10 border border-gray-100">
                    <h2 className="text-4xl font-bold text-gray-900 mb-8">1. Dashboard – Your Command Center</h2>
                    <p className="text-lg leading-relaxed mb-8">
                        The Dashboard is far more than a simple welcome screen—it's your real-time operational nerve center.
                        Every glance delivers powerful, actionable insights into the health and momentum of your entire business.
                    </p>

                    <h3 className="text-3xl font-semibold text-gray-900 mt-10 mb-6">Personalized Greeting & Date</h3>
                    <p className="leading-relaxed">
                        Start your day with a warm, personalized welcome: <strong>"Good morning, [Your Name]"</strong>,
                        accompanied by the current weekday and date. This small touch creates an immediate sense of ownership and focus.
                    </p>

                    <h3 className="text-3xl font-semibold text-gray-900 mt-10 mb-6">Workflow Section – The Sales Funnel at a Glance</h3>
                    <p className="leading-relaxed mb-6">
                        Four beautifully designed statistic cards illuminate your core workflow, turning complex data into instant understanding:
                    </p>
                    <ul className="list-disc pl-8 space-y-4 text-lg">
                        <li>
                            <strong>Requests:</strong> The top of the funnel. Shows total offers submitted, highlighting accepted ones and jobs generated—revealing your true conversion power.
                        </li>
                        <li>
                            <strong>Quotes:</strong> Today's momentum. Focuses on quotes accepted today, completed jobs, and pending follow-ups—perfect for spotting daily wins.
                        </li>
                        <li>
                            <strong>Jobs:</strong> Active field operations. Displays current active jobs and, most importantly, <strong>expected net profit</strong> after surveyor allocations—a critical profitability metric.
                        </li>
                        <li>
                            <strong>Invoices:</strong> The bottom line. Total issued invoices, realized profit, and recent activity—your direct window into cash flow health.
                        </li>
                    </ul>

                    <h3 className="text-3xl font-semibold text-gray-900 mt-10 mb-6">Today's Quotes Summary</h3>
                    <p className="leading-relaxed">
                        Vibrant, color-coded cards (emerald green for accepted, deep blue for pending, neutral gray for completed) provide an instant visual breakdown of today's financial pipeline—making it effortless to celebrate progress and prioritize follow-ups.
                    </p>

                    <h3 className="text-3xl font-semibold text-gray-900 mt-10 mb-6">Business Performance – Financial Vigilance</h3>
                    <p className="leading-relaxed mb-6">
                        Never lose sight of your receivables. This section delivers uncompromising financial oversight:
                    </p>
                    <ul className="list-disc pl-8 space-y-4 text-lg">
                        <li>A sleek progress bar visualizing total outstanding amounts owed by clients.</li>
                        <li>A prioritized list of the top 5 overdue clients, with amounts and bold red "Late" indicators—prompting immediate collection action.</li>
                    </ul>
                </div>

                {/* 2. Clients */}
                <div id="clients" className="bg-white rounded-2xl shadow-md p-10 border border-gray-100">
                    <h2 className="text-4xl font-bold text-gray-900 mb-8">2. Clients Management – The Heart of Relationships</h2>
                    <p className="text-lg leading-relaxed mb-8">
                        This is where long-term success is built. The Clients section is a full-featured CRM tailored specifically for marine surveying,
                        giving you complete visibility and control over every client interaction—from first contact to repeat business.
                    </p>
                    <ul className="list-disc pl-8 space-y-4 text-lg">
                        <li>Instant stats bar: Total clients, new additions, lost accounts, and active relationships.</li>
                        <li>Smart filtering by status (Active, Lead, Lost) and lightning-fast search by name, email, or company.</li>
                        <li>Rich client profiles with embedded mini-maps, full contact details, and client type (Individual or Corporate).</li>
                        <li>Powerful tabbed interface: Active Work, Requests History, Quotes, Invoices—all in one place.</li>
                        <li>One-click "More Actions" menu: Instantly create requests, quotes, jobs, invoices, tasks, events; collect payments; view location on map; or send secure login credentials.</li>
                    </ul>
                </div>

                {/* 3. Requests */}
                <div id="requests" className="bg-white rounded-2xl shadow-md p-10 border border-gray-100">
                    <h2 className="text-4xl font-bold text-gray-900 mb-8">3. Requests – From Inquiry to Action</h2>
                    <p className="text-lg leading-relaxed mb-8">
                        Every great job begins with a request. This section captures, organizes, and accelerates the critical first step in the client journey.
                    </p>
                    <ul className="list-disc pl-8 space-y-4 text-lg">
                        <li>Quick-access client card with phone, email, and location for immediate outreach.</li>
                        <li>Precise on-site assessment scheduling with surveyor assignment and automated reminder system (30 minutes before for surveyor, 1 hour and 1 day before for client).</li>
                        <li>Dedicated media gallery for client-uploaded images and secure internal notes visible only to your team.</li>
                    </ul>
                </div>

                {/* 4. Quotes */}
                <div id="quotes" className="bg-white rounded-2xl shadow-md p-10 border border-gray-100">
                    <h2 className="text-4xl font-bold text-gray-900 mb-8">4. Quotes – Professional Proposals That Win</h2>
                    <p className="text-lg leading-relaxed mb-8">
                        Transform assessments into winning opportunities with polished, detailed quotes that reflect your expertise.
                    </p>
                    <ul className="list-disc pl-8 space-y-4 text-lg">
                        <li>Comprehensive boat details: Type, Make, Model, Year, Length, and original location.</li>
                        <li>Clear service breakdown with full pricing transparency.</li>
                        <li>Secure file attachments (licenses, damage photos, PDFs).</li>
                        <li>Robust status tracking: Draft → Pending → Accepted → Rejected.</li>
                        <li>Client-friendly acceptance directly via email link.</li>
                    </ul>
                </div>

                {/* 5. Jobs */}
                <div id="jobs" className="bg-white rounded-2xl shadow-md p-10 border border-gray-100">
                    <h2 className="text-4xl font-bold text-gray-900 mb-8">5. Jobs – Field Operations Mastery</h2>
                    <p className="text-lg leading-relaxed mb-8">
                        Once a quote is accepted, it seamlessly becomes a Job—where planning, execution, and profitability converge.
                    </p>
                    <ul className="list-disc pl-8 space-y-4 text-lg">
                        <li>Flexible multi-surveyor assignment with individual payout definition.</li>
                        <li>Real-time profitability calculator: Total quote value − surveyor shares = <strong>your net company profit</strong>.</li>
                        <li>Detailed visit instructions (e.g., "Key with marina guard, berth #12").</li>
                        <li>Full audit trail of changes and communications.</li>
                    </ul>
                </div>

                {/* 6. Invoices */}
                <div id="invoices" className="bg-white rounded-2xl shadow-md p-10 border border-gray-100">
                    <h2 className="text-4xl font-bold text-gray-900 mb-8">6. Invoices – Seamless Financial Closure</h2>
                    <p className="text-lg leading-relaxed mb-8">
                        Close the loop with professional, automated invoicing that ensures you get paid quickly and accurately.
                    </p>
                    <ul className="list-disc pl-8 space-y-4 text-lg">
                        <li>Detailed line items with quantities, rates, and subtotals.</li>
                        <li>Automatic tax calculation and grand total summary.</li>
                        <li>Status progression: Draft → Sent → Paid → Overdue (with alerts).</li>
                        <li>One-click PDF export and email delivery.</li>
                        <li>Duplicate functionality for recurring clients.</li>
                    </ul>
                </div>

                {/* 7. Schedule */}
                <div id="schedule" className="bg-white rounded-2xl shadow-md p-10 border border-gray-100">
                    <h2 className="text-4xl font-bold text-gray-900 mb-8">7. Schedule & Calendar – Intelligent Time Management</h2>
                    <p className="text-lg leading-relaxed mb-8">
                        A world-class scheduling engine that eliminates conflicts and optimizes field efficiency.
                    </p>
                    <ul className="list-disc pl-8 space-y-4 text-lg">
                        <li>Multiple views: Daily, Weekly, Monthly.</li>
                        <li>Color-coded surveyor filtering for instant clarity.</li>
                        <li>Intuitive drag-and-drop rescheduling.</li>
                        <li>Unscheduled tasks sidebar for quick planning.</li>
                        <li>Integrated map panel showing all daily job locations—perfect for route optimization and fuel savings.</li>
                    </ul>
                </div>

                {/* 8. Surveyors */}
                <div id="surveyors" className="bg-white rounded-2xl shadow-md p-10 border border-gray-100">
                    <h2 className="text-4xl font-bold text-gray-900 mb-8">8. Surveyors & Payouts – Empowering Your Team</h2>
                    <p className="text-lg leading-relaxed mb-8">
                        Your surveyors are your greatest asset. This section ensures they are managed professionally and paid promptly.
                    </p>
                    <ul className="list-disc pl-8 space-y-4 text-lg">
                        <li>Complete profiles: Specialization, licenses, experience, geographic zone.</li>
                        <li>Pending payout tracking per surveyor.</li>
                        <li>Cash request portal: Surveyors submit requests → Admin reviews linked completed jobs → Approves with bank transfer details (IBAN, Swift).</li>
                        <li>Full transparency and audit-ready records.</li>
                    </ul>
                </div>

                {/* 9. Reports */}
                <div id="reports" className="bg-white rounded-2xl shadow-md p-10 border border-gray-100">
                    <h2 className="text-4xl font-bold text-gray-900 mb-8">9. Reports – Archiving Excellence</h2>
                    <p className="text-lg leading-relaxed">
                        Every completed survey is permanently archived with full category coverage, providing legal protection and quality assurance for years to come.
                    </p>
                </div>

                {/* 10. Categories */}
                <div id="categories" className="bg-white rounded-2xl shadow-md p-10 border border-gray-100">
                    <h2 className="text-4xl font-bold text-gray-900 mb-8">10. Categories & Subcategories – Structured Expertise</h2>
                    <p className="text-lg leading-relaxed mb-8">
                        Build standardized inspection templates that ensure nothing is ever missed.
                    </p>
                    <ul className="list-disc pl-8 space-y-4 text-lg">
                        <li>Main categories (Engine, Hull, Electrical, Plumbing) with custom colors.</li>
                        <li>Deep subcategories (e.g., Engine → Oil Level, Filters, Belts, Coolant).</li>
                        <li>Used to generate consistent, professional survey reports.</li>
                    </ul>
                </div>

                {/* 11. Settings */}
                <div id="settings" className="bg-white rounded-2xl shadow-md p-10 border border-gray-100">
                    <h2 className="text-4xl font-bold text-gray-900 mb-8">11. Settings – Tailoring the System to Perfection</h2>
                    <p className="text-lg leading-relaxed mb-8">
                        Make the platform truly yours with company-wide configurations.
                    </p>
                    <ul className="list-disc pl-8 space-y-4 text-lg">
                        <li>Business hours and holiday calendar.</li>
                        <li>Currency, date format, and tax rate settings.</li>
                        <li>Company logo and contact details (appear on all quotes and invoices).</li>
                    </ul>
                </div>

                {/* 12. Notifications */}
                <div id="notifications" className="bg-white rounded-2xl shadow-md p-10 border border-gray-100">
                    <h2 className="text-4xl font-bold text-gray-900 mb-8">12. Notifications – Stay Ahead in Real Time</h2>
                    <p className="text-lg leading-relaxed">
                        Never miss a critical update: new requests, quote acceptances, job changes, overdue payments—all delivered instantly.
                    </p>
                </div>

                {/* 13. Lead Sources */}
                <div id="lead-sources" className="bg-white rounded-2xl shadow-md p-10 border border-gray-100">
                    <h2 className="text-4xl font-bold text-gray-900 mb-8">13. Lead Sources – Marketing Intelligence</h2>
                    <p className="text-lg leading-relaxed mb-8">
                        Optimize your marketing strategy by tracking exactly where your business comes from.
                    </p>
                    <ul className="list-disc pl-8 space-y-4 text-lg">
                        <li><strong>Manage Channels:</strong> Add, edit, or delete sources (e.g., "Google Ads," "Referrals," "Boat Shows").</li>
                        <li><strong>Searchable List:</strong> Quickly find and update sources to keep your data clean.</li>
                        <li><strong>Strategic Insight:</strong> Use these tags to analyze which channels deliver high-value clients.</li>
                    </ul>
                </div>

                {/* 14. Offers Management */}
                <div id="offers-management" className="bg-white rounded-2xl shadow-md p-10 border border-gray-100">
                    <h2 className="text-4xl font-bold text-gray-900 mb-8">14. Offers Management – Proactive Sales</h2>
                    <p className="text-lg leading-relaxed mb-8">
                        Take control of your outgoing proposals. This dedicated section lets you craft, send, and track offers to close deals faster.
                    </p>
                    <ul className="list-disc pl-8 space-y-4 text-lg">
                        <li><strong>Custom Proposals:</strong> Create offers for specific requests with tailored pricing, duration (days), and notes.</li>
                        <li><strong>Flexible Agreements:</strong> Choose between digital "Signature" or simple "Text" agreement types.</li>
                        <li><strong>Status Tracking:</strong> Monitor offers as they move from "Pending" to "Accepted."</li>
                        <li><strong>Edit Capabilities:</strong> Refine pending offers or delete them if terms change.</li>
                    </ul>
                </div>

                {/* 15. My Payments */}
                <div id="payments" className="bg-white rounded-2xl shadow-md p-10 border border-gray-100">
                    <h2 className="text-4xl font-bold text-gray-900 mb-8">15. My Payments – Cash Flow Control</h2>
                    <p className="text-lg leading-relaxed mb-8">
                        A focused financial view for tracking incoming revenue and managing quote settlements.
                    </p>
                    <ul className="list-disc pl-8 space-y-4 text-lg">
                        <li><strong>Payment Status:</strong> Instantly see which quotes are "Paid" vs. "Unpaid."</li>
                        <li><strong>Financial Breakdown:</strong> View Total Amount, Paid Amount, and Remaining Balance for every quote.</li>
                        <li><strong>Manual Override:</strong> Use the "Mark as Paid" feature to record offline payments (cash, check) and update the system instantly.</li>
                    </ul>
                </div>

                {/* 16. Problem Reports */}
                <div id="problem-reports" className="bg-white rounded-2xl shadow-md p-10 border border-gray-100">
                    <h2 className="text-4xl font-bold text-gray-900 mb-8">16. Problem Reports – Field Issue Resolution</h2>
                    <p className="text-lg leading-relaxed mb-8">
                        Your early warning system. Empower surveyors to report critical on-site issues immediately, ensuring nothing slips through the cracks.
                    </p>
                    <ul className="list-disc pl-8 space-y-4 text-lg">
                        <li><strong>Live Feed:</strong> Real-time list of reported issues from the field.</li>
                        <li><strong>Detailed Insights:</strong> View full report narratives, timestamps, and reporting surveyor details.</li>
                        <li><strong>Search & Filter:</strong> specific incidents by ID, surveyor email, or keyword content to identify recurring problems.</li>
                        <li><strong>Acknowledge:</strong> Review and clear reports to maintain operational hygiene.</li>
                    </ul>
                </div>

                {/* 17. Team Members */}
                <div id="team-members" className="bg-white rounded-2xl shadow-md p-10 border border-gray-100">
                    <h2 className="text-4xl font-bold text-gray-900 mb-8">17. Team Members – Access & Roles</h2>
                    <p className="text-lg leading-relaxed mb-8">
                        Securely scale your organization by managing staff accounts and access privileges with precision.
                    </p>
                    <ul className="list-disc pl-8 space-y-4 text-lg">
                        <li><strong>Staff Management:</strong> Invite new team members or remove access for former employees.</li>
                        <li><strong>Granular Permissions:</strong> Assign specific rights (e.g., "View Only," "Can Create Quotes," "Admin Access") to ensure data security.</li>
                        <li><strong>Role Visibility:</strong> Quickly audit who has access to sensitive parts of the system.</li>
                    </ul>
                </div>

                {/* 18. User Agreements */}
                <div id="user-agreements" className="bg-white rounded-2xl shadow-md p-10 border border-gray-100">
                    <h2 className="text-4xl font-bold text-gray-900 mb-8">18. User Agreements – Contract Archive</h2>
                    <p className="text-lg leading-relaxed mb-8">
                        A digital vault for your legal documents. Ensure compliance and effortless record-keeping.
                    </p>
                    <ul className="list-disc pl-8 space-y-4 text-lg">
                        <li><strong>Document Access:</strong> View and download signed PDFs directly from the dashboard.</li>
                        <li><strong>Status Indicators:</strong> Track which agreements are "Signed" and which are "Pending Signature."</li>
                        <li><strong>Contextual Data:</strong> See associated price, client name, and agreement type at a glance.</li>
                    </ul>
                </div>

                {/* 19. Contact Messages */}
                <div id="contact-messages" className="bg-white rounded-2xl shadow-md p-10 border border-gray-100">
                    <h2 className="text-4xl font-bold text-gray-900 mb-8">19. Contact Messages – Centralized Inbox</h2>
                    <p className="text-lg leading-relaxed mb-8">
                        Capture every opportunity. A centralized hub for all inquiries coming through your public contact form.
                    </p>
                    <ul className="list-disc pl-8 space-y-4 text-lg">
                        <li><strong>Unified Inbox:</strong> No need to check email; view all web inquiries here.</li>
                        <li><strong>Search & Find:</strong> Locate messages by sender name or email address instantly.</li>
                        <li><strong>Actionable Details:</strong> View full message content and timestamp to prioritize follow-ups.</li>
                    </ul>
                </div>

                {/* 20. Live Chat */}
                <div id="live-chat" className="bg-white rounded-2xl shadow-md p-10 border border-gray-100">
                    <h2 className="text-4xl font-bold text-gray-900 mb-8">20. Live Chat – Real-Time Support</h2>
                    <p className="text-lg leading-relaxed mb-8">
                        Deliver world-class service with instant client communication.
                    </p>
                    <ul className="list-disc pl-8 space-y-4 text-lg">
                        <li><strong>Active Dashboard:</strong> Monitor ongoing conversations and waiting clients.</li>
                        <li><strong>Quick Replies:</strong> Use canned responses to answer common questions in seconds.</li>
                        <li><strong>Client Context:</strong> See who you are talking to, with integrated user details.</li>
                        <li><strong>Pending Alerts:</strong> "Pending Replies" counters ensure no client is left waiting.</li>
                    </ul>
                </div>

                {/* 21. Pro Tips */}
                <div id="tips" className="bg-gradient-to-r from-emerald-50 to-blue-50 rounded-2xl shadow-md p-10">
                    <h2 className="text-4xl font-bold text-gray-900 mb-8">21. Pro Tips & Best Practices</h2>
                    <ul className="list-disc pl-8 space-y-6 text-lg">
                        <li><strong>Color Language:</strong> Green = success/paid, Orange = pending/draft, Red = overdue/rejected—train your eye for instant status recognition.</li>
                        <li><strong>Clickable Intelligence:</strong> Almost every number on the Dashboard is a smart link—click to drill down instantly.</li>
                        <li><strong>Internal Notes Are Gold:</strong> Document everything—special client requests, access instructions, team communications. They protect you and improve service.</li>
                        <li><strong>Chase Receivables Relentlessly:</strong> Use the Business Performance section daily to follow up on red-flagged overdue invoices.</li>
                        <li><strong>Plan Geographically:</strong> Use the Schedule map view to group nearby jobs and save time/fuel.</li>
                    </ul>
                </div>
            </div>

            <div className="flex justify-center pt-12 border-t border-gray-200">
                <Link
                    to="/profile"
                    className="inline-flex items-center gap-3 px-8 py-4 bg-gradient-to-r from-blue-600 to-emerald-600 text-white text-lg font-semibold rounded-xl hover:from-blue-700 hover:to-emerald-700 transition shadow-lg"
                >
                    <ChevronLeftIcon className="w-6 h-6" />
                    Back to Dashboard
                </Link>
            </div>
        </section>
    );
}